import { Component, OnInit, ChangeDetectionStrategy, ApplicationRef } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MaintenanceService } from './authentication/helpers/maintenance.service';
import { environment } from 'src/environments/environment';
import { WebSocketService } from './modules/general/services/websocket.service';
import { AccountService } from './modules/api';
import { NotificationStateService } from './modules/general/services/notification-state.service';
import { NotificationSilenceService } from './modules/general/services/notification-silence.service';
import { NotificationToastComponent, NotificationToastData } from './modules/general/components/notification-toast/notification-toast.component';
import { DateTime } from 'luxon';
import { concat, filter, first, fromEvent, interval, merge } from 'rxjs';

const WS_EVENT_LABEL = 'AccountNotificationCreated';
const SW_UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterModule]
})
export class AppComponent implements OnInit {

  private toastQueue: NotificationToastData[] = [];
  private toastActive = false;
  private notificationsListenerAttached = false;
  private lastNotificationEmployeeId: number | null = null;

  constructor(
    private maintenanceService: MaintenanceService,
    private router: Router,
    private appRef: ApplicationRef,
    private snackBar: MatSnackBar,
    private swUpdate: SwUpdate,
    private webSocketService: WebSocketService,
    private accountService: AccountService,
    private notificationState: NotificationStateService,
    private silenceService: NotificationSilenceService
  ) {}

  ngOnInit() {
    this.subscribeToMaintenance();
    this.checkForServiceWorkerUpdate();
    this.subscribeToAccountNotifications();

    // The employeeId isn't known yet if the app boots on the login page (SPA
    // navigation after login doesn't recreate this component), and it can
    // change if a different employee logs in later in the same session -
    // re-check on every navigation.
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.subscribeToAccountNotifications();
      }
    });
  }

  private getEmployeeId(): number | null {
    const info = JSON.parse(localStorage.getItem('authUserInformation') || '{}');
    const id = info?.Employee?.id;
    // Coerce defensively: the backend has been observed sending this as a
    // string, even though it's typed as `number` (authUserModel.Employee.id)
    // - as a string it wouldn't match a numeric filter server-side.
    return id === undefined || id === null || id === '' ? null : Number(id);
  }

  private subscribeToAccountNotifications(): void {
    if (!environment.enableWebsocket || !environment.enableNotifications) return;
    const employeeId = this.getEmployeeId();
    if (employeeId === null) return;

    if (employeeId !== this.lastNotificationEmployeeId) {
      this.lastNotificationEmployeeId = employeeId;
      this.webSocketService.registerPersistentEvent(WS_EVENT_LABEL, [{ type: 'employeeId', value: employeeId }], []);
      this.webSocketService.subscribePersistentEvents();
    }

    if (this.notificationsListenerAttached) return;
    this.notificationsListenerAttached = true;

    this.webSocketService.onMessage().subscribe(msg => {
      const event = (msg as any)?.event;
      if (event?.label !== WS_EVENT_LABEL) return;

      const currentEmployeeId = this.getEmployeeId();
      if (currentEmployeeId === null || Number(event?.data?.employeeId) !== currentEmployeeId) return;

      const notificationId: number = event.data.notificationId;
      this.accountService.getNotifications(1, 1, undefined, { AND: [['id', 'eq', notificationId]] }).subscribe({
        next: (res) => {
          const notifications = res.data as { id: number; message: string; readAt: string | null }[];
          if (!notifications?.length) return;
          this.notificationState.setUnreadCount(this.notificationState.currentCount + 1);
          if (!this.silenceService.isSilenced) {
            this.queueToast({ message: notifications[0].message, notificationId: notifications[0].id });
          }
        }
      });
    });
  }

  private queueToast(data: NotificationToastData): void {
    this.toastQueue.push(data);
    if (!this.toastActive) {
      this.showNextToast();
    }
  }

  private showNextToast(): void {
    if (!this.toastQueue.length) {
      this.toastActive = false;
      return;
    }
    this.toastActive = true;
    const data = this.toastQueue.shift()!;
    const ref = this.snackBar.openFromComponent(NotificationToastComponent, {
      data,
      duration: 0,
      verticalPosition: 'bottom',
      horizontalPosition: 'end',
      panelClass: 'notification-snackbar',
    });
    ref.afterDismissed().subscribe(({ dismissedByAction }) => {
      if (dismissedByAction) {
        const readAt = DateTime.now().toFormat('yyyy-MM-dd HH:mm');
        this.accountService.updateNotification(data.notificationId, { readAt }).subscribe({
          next: () => this.notificationState.setUnreadCount(Math.max(0, this.notificationState.currentCount - 1))
        });
      }
      this.showNextToast();
    });
  }

  private subscribeToMaintenance() {
    this.maintenanceService.isMaintenance$.subscribe((isMaintenance) => {
      // If maintenance mode is enabled and the current route is not '/maintenance', navigate to '/maintenance'
      if (isMaintenance && this.router.url !== '/maintenance') {
        this.router.navigate(['/maintenance']);
      }
    });
  }

  private checkForServiceWorkerUpdate() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(event => {
        if (event.type === 'VERSION_READY') {
          this.showUpdateNotification();
          console.log('New version available');
        }
      });

      // The SW lost the files of the running version (e.g. cache evicted) - lazy chunks can no longer load
      this.swUpdate.unrecoverable.subscribe(() => this.showReloadNotification());

      // Tabs are often left open for weeks; also re-check periodically and when the tab becomes visible
      // again, otherwise they keep running an old build whose chunks are gone from the server.
      const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable));
      const tabVisible$ = fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'));
      merge(concat(appIsStable$, interval(SW_UPDATE_CHECK_INTERVAL_MS)), tabVisible$).subscribe(() => {
        this.swUpdate.checkForUpdate().catch(err => console.warn('Update check failed', err));
      });
    }
  }

  private showReloadNotification() {
    const snackBarRef = this.snackBar.open('De app moet opnieuw geladen worden', 'Herladen', {
      duration: 0,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
    snackBarRef.onAction().subscribe(() => window.location.reload());
  }

  private showUpdateNotification() {
    const snackBarRef = this.snackBar.open('Er is een nieuwe frontend versie beschikbaar', 'Nu bijwerken', {
      duration: 0, // Make the snackbar persistent until manually dismissed
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
    snackBarRef.onAction().subscribe(() => {
      this.swUpdate.activateUpdate().then(() => {
        console.log('Activating new version');
        // Reload the page to activate the new version
        window.location.reload();
      });
    });
  }
}
